// src/pages/Pricing.js
import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { createOrderApi, verifyPaymentApi } from "../../apis/payments.api";
// Trial-related imports removed - trial only available in SEO Analyzer
// import { initiateSeoTrialPaymentApi, verifySeoTrialPaymentApi } from "../../apis/payments.api";
import { useDarkMode } from "../../hooks/useDarkMode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";


const PricingPage = () => {
  // billingCycle: 'monthly' | 'yearly'
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loadingPlan, setLoadingPlan] = useState(null);
  // const [loadingTrial, setLoadingTrial] = useState(false); // Trial removed from pricing page
  const [error, setError] = useState(null);
  const [usdToInr, setUsdToInr] = useState(83); // fallback if API fails
  const [rateLoading, setRateLoading] = useState(false);

  // Fetch USD→INR rate from open.er-api.com
  // useEffect(() => {
  //   const fetchRate = async () => {
  //     try {
  //       setRateLoading(true);
  //       const res = await fetch('https://open.er-api.com/v6/latest/USD');
  //       const data = await res.json();
  //       const rate = data?.rates?.INR;
  //       if (rate && typeof rate === 'number') {
  //         setUsdToInr(rate);
  //         // console.log('Fetched USD→INR rate:', rate);
  //       } else {
  //         // console.warn('USD→INR rate missing, using fallback', usdToInr);
  //       }
  //     } catch (e) {
  //       // console.warn('Failed to fetch USD→INR rate, using fallback', usdToInr, e);
  //     } finally {
  //       setRateLoading(false);
  //     }
  //   };
  //   fetchRate();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Base pricing (actual, before discount)
  // Monthly actual prices derived from yearly/12 for display
  const basePlans = [
    {
      title: "Lite",
      yearlyActual: 144,
      yearlyDiscountPercent: 0,
      monthlyActual: 15,
      creditsPerMonth: 300,
      desc: "Best for beginners just starting out.",
      features: [
        "✔️ Core SEO & Content ",
        "✔️ Backlink Analysis",
        "✔️ Traffic Analytics",
        "✔️ Keyword Research",
        "✔️ Competitor Analysis",
        "✔️ SERP Analysis",
        "✔️ Brand Monitoring",
        "✔️ Content and Others",
        "✔️ AI Analysis",
      ],
      buttonText: "Buy Now",
      buttonColor: "bg-gray-700 hover:bg-gray-800",
      highlight: false,
      highlightColor: "border-gray-400",
    },
    {
      title: "Standard",
      subtitle: "Most Popular",
      yearlyActual: 480,
      yearlyDiscountPercent: 0,
      monthlyActual: 50,
      creditsPerMonth: 1250,
      desc: "Perfect for individuals and small teams.",
      features: [
        "✔️ Core SEO & Content ",
        "✔️ Backlink Analysis",
        "✔️ Traffic Analytics",
        "✔️ Keyword Research",
        "✔️ Competitor Analysis",
        "✔️ SERP Analysis",
        "✔️ Brand Monitoring",
        "✔️ Content and Others",
        "✔️ AI Analysis",
      ],
      buttonText: "Buy Now",
      buttonColor: "bg-green-600 hover:bg-green-700",
      highlight: true,
      highlightColor: "border-[#008300]",
    },
    {
      title: "Advanced",
      yearlyActual: 960,
      yearlyDiscountPercent: 0,
      monthlyActual: 100,
      creditsPerMonth: 3000,
      desc: "For advanced users who need everything.",
      features: [
        "✔️ Core SEO & Content ",
        "✔️ Backlink Analysis",
        "✔️ Traffic Analytics",
        "✔️ Keyword Research",
        "✔️ Competitor Analysis",
        "✔️ SERP Analysis",
        "✔️ Brand Monitoring",
        "✔️ Content and Others",
        "✔️ AI Analysis",
      ],
      buttonText: "Buy Now",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      highlight: false,
      highlightColor: "border-blue-600",
    },
  ];

  // Compute display plans based on billing cycle
  const plans = useMemo(() => {
    return basePlans.map((p) => {
      const monthlyPrice = Math.round(p.monthlyActual);
      const yearlyPrice = Math.round(p.yearlyActual);
      const creditsMonthly =
        p.creditsPerMonth ??
        (billingCycle === "monthly"
          ? Math.round((p.yearlyActual * 100) / 12)
          : Math.round(p.yearlyActual * 100));

      let priceLabel;
      let originalLabel;
      let showStrikeOriginal = false;
      let savingsAmount = 0;
      if (billingCycle === "yearly") {
        priceLabel = `$${yearlyPrice} / Year`;
        originalLabel = `$${monthlyPrice * 12} / Year`;
        savingsAmount = monthlyPrice * 12 - yearlyPrice;
        showStrikeOriginal = savingsAmount > 0;
      } else {
        priceLabel = `$${monthlyPrice} / Month`;
      }

      return {
        ...p,
        priceLabel,
        originalLabel,
        showStrikeOriginal,
        savingsAmount,
        creditsLabel: `${creditsMonthly} Credits / Per month`,
      };
    });
  }, [billingCycle, basePlans]);

  // Trial payment handler removed - trial only available in SEO Analyzer
  /* const handleTrialPayment = async () => {
    console.log('[TRIAL] Starting trial payment');
    try {
      setError(null);
      setLoadingTrial(true);

      const { listProjectsApi } = await import('../../apis/projects.api');
      const projectsResponse = await listProjectsApi();
      const projects = projectsResponse.data?.projects || projectsResponse.data || [];
      
      if (!projects || projects.length === 0) {
        setError('No projects found. Please create a project first before purchasing trial.');
        toast.error('❌ Please create a project first before purchasing trial.');
        setLoadingTrial(false);
        return;
      }

      const firstProject = projects[0];
      const projectId = firstProject.id || firstProject.project_id;
      console.log('[TRIAL] Using project:', projectId, firstProject);

      const orderResponse = await initiateSeoTrialPaymentApi({
        project_id: projectId
      });

      console.log('[TRIAL] Order created:', orderResponse.data);

      const { order_id, amount, amount_inr, exchange_rate, key } = orderResponse.data;

      if (!window.Razorpay) {
        throw new Error('Razorpay SDK not loaded. Please refresh the page.');
      }

      const options = {
        key: key || process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_key",
        amount: Math.round(amount_inr * 100),
        currency: 'INR',
        name: 'Novarsis AIO',
        description: 'SEO Analysis Trial - 1 Page Free',
        order_id: order_id,
        handler: async (response) => {
          console.log('[TRIAL] Payment successful, verifying:', response);
          try {
            const verifyResponse = await verifySeoTrialPaymentApi({
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              project_id: projectId,
              amount: amount,
              exchange_rate: exchange_rate
            });

            console.log('[TRIAL] Payment verified:', verifyResponse.data);

            if (verifyResponse.status === 200 || verifyResponse.data.success) {
              toast.success(
                `✅ Trial payment successful! You can now analyze 1 page. Redirecting...`,
                { autoClose: 5000 }
              );
              setLoadingTrial(false);
              
              setTimeout(() => {
                localStorage.removeItem('trialEligibility');
                navigate('/dashboard', { replace: true });
                window.location.reload();
              }, 1500);
            }
          } catch (verifyError) {
            console.error('[TRIAL] Verification failed:', verifyError);
            setError(
              verifyError.response?.data?.error ||
              verifyError.response?.data?.message ||
              'Trial payment verification failed. Please contact support.'
            );
            toast.error('❌ Trial payment verification failed');
            setLoadingTrial(false);
          }
        },
        prefill: {
          name: localStorage.getItem('userName') || '',
          email: localStorage.getItem('userEmail') || '',
          contact: ''
        },
        theme: {
          color: '#008300'
        },
        modal: {
          ondismiss: () => {
            console.log('[TRIAL] Payment modal dismissed');
            setLoadingTrial(false);
          }
        }
      };

      console.log('[TRIAL] Opening Razorpay modal');
      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', (response) => {
        console.error('[TRIAL] Payment failed:', response.error);
        setError(`Trial payment failed: ${response.error.description}`);
        toast.error(`❌ Payment failed: ${response.error.description}`);
        setLoadingTrial(false);
      });

      rzp.open();
    } catch (err) {
      console.error('[TRIAL] Error:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to initiate trial payment';
      
      setError(errorMessage);
      toast.error(`❌ ${errorMessage}`);
      setLoadingTrial(false);
    }
  }; */

  const handleBuyNow = async (plan) => {
    // console.log("Buy Now clicked for plan:", plan.title);
    try {
      setError(null);
      setLoadingPlan(plan.title);

      // Determine amount based on billing cycle
      const amount =
        billingCycle === "yearly"
          ? Math.round(
              plan.yearlyActual * (1 - (plan.yearlyDiscountPercent || 0) / 100),
            )
          : Math.round(plan.monthlyActual);

      // console.log("Calculated amount:", amount, "Billing cycle:", billingCycle);

      // Create order from backend
      // console.log("Creating order with backend - sending USD amount:", amount);
      // Backend will convert USD to INR using live exchange rate
      const orderResponse = await createOrderApi({
        amount: amount, // Send USD - backend converts to INR
        currency: "USD",
        plan_name: plan.title,
        billing_cycle: billingCycle,
      });

      // console.log("Order response:", orderResponse);
      const { order_id: orderId, amount: orderAmount } = orderResponse.data;
      // console.log("Order ID:", orderId, "Order Amount:", orderAmount);

      // Initialize Razorpay payment
      // console.log("Razorpay Key:", process.env.REACT_APP_RAZORPAY_KEY_ID);

      if (!window.Razorpay) {
        throw new Error("Razorpay SDK not loaded. Please refresh the page.");
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_key",
        amount: orderAmount,
        currency: "INR",
        name: "Novarsis AIO",
        description: `${plan.title} Plan - ${billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}`,
        order_id: orderId,
        handler: async (response) => {
          // console.log("Payment response:", response);
          try {
            // Verify payment with backend
            const verifyResponse = await verifyPaymentApi({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_name: plan.title,
              amount: amount, // Send USD amount
              billing_cycle: billingCycle,
            });

            if (verifyResponse.status === 200) {
              alert(
                `Payment successful! You have purchased ${plan.title} plan.`,
              );
              setLoadingPlan(null);
              window.location.href = "/dashboard/account-info/current-plan";
            }
          } catch (verifyError) {
            setError("Payment verification failed. Please contact support.");
            // console.error("Verification error:", verifyError);
            setLoadingPlan(null);
          }
        },
        prefill: {
          name: "",
          email: "",
          contact: "",
        },
        theme: {
          color: "#0284C7",
        },
        modal: {
          ondismiss: () => {
            setLoadingPlan(null);
          },
        },
      };

      // console.log("Opening Razorpay modal with options:", options);
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        // console.error("Payment failed:", response.error);
        setError(`Payment failed: ${response.error.description}`);
        setLoadingPlan(null);
      });
      rzp.open();
      // console.log("Razorpay modal opened");
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to initiate payment. Please try again.";
      setError(errorMessage);
      // console.error("Payment error:", err);
      // console.error("Error details:", err.response?.data);
      setLoadingPlan(null);
    }
  };

  return (
    <>
    
<Helmet>

  {/* Title */}
  <title>Pricing Plans | Seo Up Rank AI SEO Tools</title>

  {/* Meta Description */}
  <meta
    name="description"
    content="Explore Seo Up Rank pricing plans for AI powered SEO tools, website optimization, keyword research, site audit and advanced SEO analytics."
  />

  {/* Keywords */}
  <meta
    name="keywords"
    content="Seo Up Rank pricing, SEO tools pricing, AI SEO platform plans, website optimization pricing, SEO audit tools pricing"
  />

  {/* Robots */}
  <meta name="robots" content="index, follow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/pricing" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Pricing Plans" />
  <meta
    property="og:description"
    content="Choose the best pricing plan for Seo Up Rank AI powered SEO tools and website optimization platform."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/pricing" />
  <meta property="og:site_name" content="Seo Up Rank" />
  <meta property="og:image" content="https://novarsis.ai/seo-banner.png" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Seo Up Rank Pricing" />
  <meta
    name="twitter:description"
    content="View Seo Up Rank pricing plans for AI SEO tools and website optimization."
  />
  <meta name="twitter:image" content="https://novarsis.ai/seo-banner.png" />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"Product",
      "name":"Seo Up Rank SEO Platform",
      "description":"AI powered SEO tools for website optimization, keyword research and SEO analytics.",
      "brand":{
        "@type":"Brand",
        "name":"Seo Up Rank"
      },
      "offers":{
        "@type":"Offer",
        "url":"https://novarsis.ai/pricing",
        "priceCurrency":"USD",
        "availability":"https://schema.org/InStock"
      }
    }
    `}
  </script>

</Helmet>
   
    <div
      className={`text-gray-800 overflow-hidden w-full mx-auto rounded-2xl shadow ${isDarkMode ? "bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900" : "bg-white"}`}
    >
      {/* Error Alert */}
      {error && (
        <div
          className={`border-l-4 p-4 mx-6 mt-4 ${isDarkMode ? "bg-red-900/30 border-red-600" : "bg-red-50 border-red-600"}`}
        >
          <p className={isDarkMode ? "text-red-300" : "text-red-700"}>
            {error}
          </p>
          <button
            onClick={() => setError(null)}
            className={`text-sm mt-2 ${isDarkMode ? "text-red-400 hover:text-red-300" : "text-red-600 hover:text-red-800"}`}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ================== HERO SECTION ================== */}
      <section
        className={`text-center pt-20 px-6 relative overflow-hidden ${isDarkMode ? "bg-gradient-to-br from-gray-800 to-gray-900" : "bg-white"}`}
      >
        <motion.h1
          className={`text-4xl sm:text-5xl font-extrabold mb-4 ${isDarkMode ? "text-[#008300]" : "text-[#008300]"}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Simple & Transparent Pricing
        </motion.h1>
        <motion.p
          className={`max-w-2xl mx-auto mb-10 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          Choose the perfect plan for your SEO automation journey. Upgrade
          anytime as your business grows.
        </motion.p>

        {/* Billing Toggle */}
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-2 rounded-full border shadow-sm ${
              billingCycle === "monthly"
                ? "bg-[#008300] text-white border-[#006400]"
                : isDarkMode
                  ? "bg-gray-700 text-[#008300] border-gray-600"
                  : "bg-white text-[#008300] border-[#006400]"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`px-4 py-2 rounded-full border shadow-sm ${
              billingCycle === "yearly"
                ? "bg-[#008300] text-white border-[#006400]"
                : isDarkMode
                  ? "bg-gray-700 text-green-500 border-gray-600"
                  : "bg-white text-green-500 border-[#006400]"
            }`}
          >
            Yearly
          </button>
        </div>

        {/* ================== TRIAL CARD - SPECIAL OFFER ================== */}
        {/* Trial plan is only available in SEO Analyzer - removed from pricing page */}
        {/* <section className="max-w-4xl mx-auto mt-9 mb-16 px-6">
          <motion.div
            className={`relative backdrop-blur-lg shadow-2xl rounded-2xl p-8 border-4 border-yellow-400 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-yellow-900/30 to-orange-900/30' 
                : 'bg-gradient-to-br from-yellow-50 to-orange-50'
            }`}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg animate-pulse">
              🚀 ONE-TIME TRIAL OFFER
            </span>

            <div className="grid md:grid-cols-2 gap-8 items-center mt-4">
              <div>
                <h3 className={`text-3xl font-bold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-orange-600'}`}>
                  Try SEO Analysis for Free
                </h3>
                <p className={`text-lg mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Test our complete SEO analysis on <strong>1 page</strong> before committing to a plan.
                </p>
                
                <ul className={`space-y-2 mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>Full SEO audit for 1 page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>Detailed recommendations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>One-time offer per domain</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>No subscription required</span>
                  </li>
                </ul>

                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  💡 Perfect for testing before choosing a plan
                </div>
              </div>

              <div className="text-center">
                <div className={`text-6xl font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-orange-600'}`}>
                  Free
                </div>
                <div className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  One-time payment
                </div>

                <button
                  onClick={handleTrialPayment}
                  disabled={loadingTrial}
                  className={`w-full py-4 px-6 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold rounded-xl shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    loadingTrial ? 'cursor-not-allowed' : ''
                  }`}
                >
                  {loadingTrial ? (
                    <>
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                      Processing...
                    </>
                  ) : (
                    'Start Your Trial'
                  )}
                </button>

                <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Available when you run your first SEO analysis
                </p>
              </div>
            </div>
          </motion.div>
        </section> */}

        {/* ================== PRICING CARDS ================== */}
        <section className="py-20 px-6 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 justify-center">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              className={`relative backdrop-blur-lg shadow-xl rounded-2xl p-8 border-t-8 ${plan.highlightColor} ${
                plan.highlight ? "scale-105 shadow-2xl" : ""
              } hover:-translate-y-2 transition-all duration-500 ${
                isDarkMode
                  ? "bg-gray-800/80 hover:shadow-[#008300]/60"
                  : "bg-white/80 hover:shadow-[#008300]/60"
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
            >
              {plan.subtitle && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-b-lg shadow-md animate-pulse">
                  {plan.subtitle}
                </span>
              )}

              <h3
                className={`text-2xl py-1 font-bold text-center mb-2 ${isDarkMode ? "text-[#008300]" : "text-[#008300]"}`}
              >
                {plan.title}
              </h3>
              {/* Original yearly price (struck-through, red, above discounted) when yearly selected */}
              {plan.showStrikeOriginal && (
                <div className="text-center pt-1">
                  <span className="text-red-600 line-through font-semibold">
                    {plan.originalLabel}
                  </span>
                  <span className="ml-2 text-green-600 text-sm">
                    Save ${plan.savingsAmount}
                  </span>
                </div>
              )}
              <h2
                className={`text-3xl pb-1 font-extrabold text-center mb-2 ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}
              >
                {plan.priceLabel}
              </h2>
              <p
                className={`py-1 text-center mb-6 font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {plan.creditsLabel}
              </p>

              <ul
                className={`space-y-3 mb-6 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleBuyNow(plan)}
                disabled={loadingPlan === plan.title}
                className={`${plan.buttonColor} text-white w-full py-3 rounded-lg font-semibold transition duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              >
                {loadingPlan === plan.title ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Processing...
                  </>
                ) : (
                  plan.buttonText
                )}
              </motion.button>
            </motion.div>
          ))}
        </section>
      </section>
    </div> </>
  );
};

export default PricingPage;
